import { useAuthentication, useQueryParam } from '@psyomics/ui-common';
import { Header, MfaFieldForm } from '@psyomics/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetServiceUserQuery } from '../../account/hooks/use-get-service-user-query';
import { confirmMfaCode, requestMfaCode, resendMfaCode } from '../mfa-client';

const MfaPhoneStep = ({
  onSubmit,
  navigateToCode,
}: {
  readonly onSubmit: (phone: string) => Promise<void>;
  readonly navigateToCode: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="form-container">
      <Header level="h2">{t('page.mfaSetup.phone.title')}</Header>
      <MfaFieldForm
        autoComplete="tel"
        buttonLabel={t('page.mfaSetup.phone.submit')}
        description={t('page.mfaSetup.phone.description')}
        errorInvalid={t('auth.common.errors.phoneNumberInvalid')}
        errorRequired={t('auth.common.errors.mobileNumberRequired')}
        inputId="phoneNumber"
        inputLabel={t('page.mfaSetup.phone.label')}
        navigateToCode={navigateToCode}
        onSubmit={onSubmit}
        placeholder={t('page.mfaSetup.phone.placeholder')}
      />
    </div>
  );
};

const MfaCodeStep = ({
  phoneNumber,
  onSubmit,
  onResend,
  onChangeNumber,
  onStartAgain,
  isSelfReferral = false,
}: {
  readonly phoneNumber: string | null;
  readonly onSubmit: (code: string) => Promise<void>;
  readonly onResend: () => void | Promise<void>;
  readonly onChangeNumber: () => void;
  readonly onStartAgain: () => void;
  readonly isSelfReferral?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="form-container space-y-6">
      <Header level="h2">{t('page.mfaSetup.code.title')}</Header>
      <MfaFieldForm
        autoComplete="one-time-code"
        buttonLabel={t('page.mfaSetup.code.submit')}
        description={
          phoneNumber ? t('page.mfaSetup.code.description', { phoneNumber }) : t('auth.common.mfa.codeDescription')
        }
        errorRequired={t('auth.common.errors.confirmationCodeMissing')}
        inputId="mfaCode"
        inputLabel={
          isSelfReferral ? t('auth.register.mfa.selfReferralCodeInputLabel') : t('auth.register.mfa.codeInputLabel')
        }
        inputMode="numeric"
        onResend={onResend}
        onSubmit={onSubmit}
        placeholder={t('page.mfaSetup.code.placeholder')}
        {...(isSelfReferral ? { onStartAgain } : { onChangeNumber })}
      />
    </div>
  );
};

const MfaSetup = () => {
  const requirePhone = useQueryParam('require-phone');
  const redirect = useQueryParam('redirect');
  const { apiUrl, logout } = useAuthentication();
  const [serviceUser] = useGetServiceUserQuery();
  const navigate = useNavigate();
  const [showCode, setShowCode] = useState(requirePhone === undefined);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  const handleNavigateToCode = () => {
    setShowCode(true);
  };

  const handlePhoneSubmit = async (inputPhone: string) => {
    await requestMfaCode(apiUrl, inputPhone);
    setPhoneNumber(inputPhone);
    setShowCode(true);
  };

  const handleCodeSubmit = async (code: string) => {
    try {
      await confirmMfaCode(apiUrl, code);
      navigate(redirect ?? '/', { replace: true });
    } catch {
      throw new Error('mfa verification failed');
    }
  };

  const handleResend = async () => {
    await resendMfaCode(apiUrl);
  };

  const selfReferralCode = useMemo(() => {
    if (!redirect) return undefined;
    const code = redirect.match(/\/self-referral\/(?:init\/)?([^/?#]+)/)?.[1];
    return code;
  }, [redirect]);

  return showCode ? (
    <MfaCodeStep
      isSelfReferral={!serviceUser?.hasTrustReferral}
      onChangeNumber={() => setShowCode(false)}
      onResend={handleResend}
      onStartAgain={() => logout('self-referral/redirect', { ...(selfReferralCode ? { selfReferralCode } : {}) })}
      onSubmit={handleCodeSubmit}
      phoneNumber={phoneNumber}
    />
  ) : (
    <MfaPhoneStep navigateToCode={handleNavigateToCode} onSubmit={handlePhoneSubmit} />
  );
};

export { MfaCodeStep, MfaSetup };
